import {
  ChevronDown,
  ChevronRight,
  CircleCheck,
  CircleHelp,
  FlaskConical,
  Monitor,
  Smartphone,
} from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Sparkline } from '@/components/data/Sparkline';
import { DeviceTable } from '@/components/landing/DeviceTable';
import { type FunnelStageItem, FunnelStages } from '@/components/landing/FunnelStages';
import { formatCls, formatCount1, formatMs, formatSeconds } from '@/components/landing/format';
import { InsightCard, type InsightTone } from '@/components/landing/InsightCard';
import { CaveatRef } from '@/components/methodology/CaveatRef';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { StatCard, type StatStatus } from '@/components/ui/stat-card';
import { EmptyState, ErrorState, Skeleton, TableSkeleton } from '@/components/ui/states';
import { ACCENT } from '@/lib/chart';
import { formatBookedAt, formatMetric } from '@/lib/format';
import { useViewMessages } from '@/lib/i18n';
import { useLanding } from '@/lib/queries';
import { cn } from '@/lib/utils';

/*
 * Landingsside-intelligens — the daily LP cockpit.
 *
 * Zone 0  header + LP selector (alle | provetime | lege-anbefalt | …)
 * Zone 1  Trakten: Meta-klikk → Landet (beacon) → Åpnet booking → Booket
 * Zone 2  Sidehelse: PSI mobil/desktop, JS-feil, friksjon
 * Zone 3  Atferd: Bookingforsøk (egne beacons — per-attempt journal + watchdog),
 *         deretter Clarity-aggregatene: økter/aktiv tid/scroll/brukere, device
 *         split, per-dag. Clarity-tomtilstanden er ærlig (clarityStatus).
 * Zone 4  Anbefalinger: severity-sorted insight cards (code → nb/en template)
 * Zone 5  Historikk (collapsed): experiments + shipped changes + intake notes
 *
 * Everything renders defensively: null → em dash, empty arrays → empty states.
 */

type LandingData = NonNullable<ReturnType<typeof useLanding>['data']>;
type Playbook = LandingData['playbook'];
type SpeedSample = NonNullable<LandingData['speed']['latest']['mobile']>;

const HANDLE_ORDER = [
  'alle',
  'provetime',
  'lege-anbefalt',
  'kroniske-smerter',
  'etter-operasjon',
  'trening-over-70',
] as const;
type Handle = (typeof HANDLE_ORDER)[number];

// --- Insight templates -------------------------------------------------------
// The backend sends { code, severity, params }; copy lives client-side so it
// can be sharp, localized and cheap to iterate. Param keys are read through
// candidate lists (pickNum) so a backend rename degrades to an em dash, never
// a crash.

type InsightParams = Record<string, unknown>;

function pickNum(p: InsightParams, ...keys: string[]): number | null {
  for (const k of keys) {
    const v = p[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
  }
  return null;
}

function pctOf(num: number | null, den: number | null): number | null {
  if (num == null || den == null || den <= 0) return null;
  return (num / den) * 100;
}

const fInt = (p: InsightParams, ...keys: string[]): string =>
  formatMetric(pickNum(p, ...keys), 'int');
const fPct = (p: InsightParams, ...keys: string[]): string =>
  formatMetric(pickNum(p, ...keys), 'pct');

interface InsightCopy {
  title: string;
  body: string;
}
type InsightTemplates = Record<string, (p: InsightParams) => InsightCopy>;

const INSIGHTS_NB: InsightTemplates = {
  CLARITY_NOT_CONFIGURED: () => ({
    title: 'Koble til Clarity',
    body: 'Uten Clarity-data er du blind på atferden på siden (scroll, friksjon, JS-feil). Hent et eksport-token under Innstillinger hos Clarity → Data Export og legg det inn på serveren — resten plukkes opp automatisk.',
  }),
  LOW_LAND_RATE: (p) => ({
    title: `Lav land-rate (${fPct(p, 'landRatePct', 'ratePct', 'pct')})`,
    body: `Bare ${fPct(p, 'landRatePct', 'ratePct', 'pct')} av Meta-klikkene når landingssiden — resten faller av før siden vises. Sjekk at annonse-URL-ene peker rett på siden (ingen omdirigeringskjede) og få ned lastetiden på mobil; hvert tapte klikk er allerede betalt.`,
  }),
  SLOW_MOBILE: (p) => ({
    title: 'Treg på mobil',
    body: `Mobil-score ${fInt(p, 'perfScore', 'score')}/100 og LCP ${formatMs(pickNum(p, 'lcpMs', 'lcp'))} — over 2,5 s begynner besøkende å falle av før de ser tilbudet. Komprimer heltebildet (WebP, riktig størrelse), utsett tunge scripts og hold alt over folden lett.`,
  }),
  HIGH_FRICTION: (p) => ({
    title: 'Høy friksjon',
    body: `${fInt(p, 'rageClicks', 'rage')} rage-klikk og ${fInt(p, 'deadClicks', 'dead')} døde klikk i perioden — folk klikker på ting som ikke svarer. Se Clarity-opptakene med friksjon og gjør elementene klikkbare, eller fjern det som ser klikkbart ut uten å være det.`,
  }),
  JS_ERRORS: (p) => ({
    title: 'JS-feil på siden',
    body: `${fInt(p, 'scriptErrors', 'count', 'errors')} script-feil i perioden — én feil kan blokkere booking-modalen for hele økten. Åpne Clarity → Errors, finn meldingen og fiks den i temaet før du skrur opp budsjettet.`,
  }),
  SHALLOW_SCROLL: (p) => ({
    title: 'Grunn scroll',
    body: `Snittbruker ser bare ${fPct(p, 'scrollDepthPct', 'scrollDepth')} av siden — innholdet under helten din blir aldri sett. Flytt sosial proof + tilbudet høyere, og kutt seksjoner som ikke selger.`,
  }),
  LOW_ACTIVE_TIME: (p) => ({
    title: 'Lav aktiv tid',
    body: `Snittøkten er aktiv i bare ${formatSeconds(pickNum(p, 'activeTimeSec', 'activeTime'))} — folk skummer og forsvinner. Stram inn helteseksjonen: ett løfte, ett bevis, én knapp — alt som ikke hjelper beslutningen, skal vekk.`,
  }),
  LOW_MODAL_OPENS: (p) => {
    const rate =
      pickNum(p, 'openRatePct', 'ratePct', 'pct') ??
      pctOf(pickNum(p, 'modalOpens', 'opens'), pickNum(p, 'adLandings', 'landings', 'visits'));
    return {
      title: 'Få åpner booking',
      body: `Bare ${fInt(p, 'modalOpens', 'opens')} av ${fInt(p, 'adLandings', 'landings', 'visits')} besøk åpner booking-modalen (${formatMetric(rate, 'pct')}). CTA-en er for svak eller for langt ned — gjør den synlig uten scroll og test mer konkret knappetekst («Book gratis prøvetime»).`,
    };
  },
  QUICKBACK: (p) => ({
    title: 'Mange spretter rett ut',
    body: `${fInt(p, 'quickbacks', 'count')} økter forlater siden nesten umiddelbart — et forventningsbrudd mellom annonse og side. La overskriften gjenta løftet fra annonsen folk klikket på: samme smerte, samme ord, samme tilbud.`,
  }),
  GOOD_SHAPE: () => ({
    title: 'Siden er i god form',
    body: 'Ingen kritiske funn i perioden — hastighet, atferd og trakt ser sunne ut. Bruk energien på annonsene: nye kreativer og forsiktig opptrapping på vinnerne.',
  }),
  OVERLAY_WATCHDOG: (p) => ({
    title: 'Fullførings-signal uten booking',
    body: `${fInt(p, 'count', 'flags', 'watchdogFlags')} besøkende fikk fullførings-melding uten at noen booking finnes i Momence — se opptakene i Clarity nå.`,
  }),
  ATTEMPTS_NO_COMPLETIONS: (p) => ({
    title: 'Ingen fullføringer',
    body: `${fInt(p, 'opens', 'modalOpens', 'count')} åpnet kalenderen uten én fullføring i perioden.`,
  }),
  CLARITY_QUOTA: () => ({
    title: 'Clarity-kvoten er brukt for i dag',
    body: 'Dataene kommer automatisk etter neste natts kjøring.',
  }),
  CLARITY_NO_DATA_IN_RANGE: () => ({
    title: 'Ingen Clarity-data i valgt periode ennå',
    body: 'Clarity dekker hele dager (i går og eldre).',
  }),
};

const INSIGHTS_EN: InsightTemplates = {
  CLARITY_NOT_CONFIGURED: () => ({
    title: 'Connect Clarity',
    body: 'Without Clarity data you are blind to on-page behavior (scroll, friction, JS errors). Grab an export token under Settings in Clarity → Data Export and add it on the server — the rest is picked up automatically.',
  }),
  LOW_LAND_RATE: (p) => ({
    title: `Low land rate (${fPct(p, 'landRatePct', 'ratePct', 'pct')})`,
    body: `Only ${fPct(p, 'landRatePct', 'ratePct', 'pct')} of Meta clicks reach the landing page — the rest drop before the page renders. Make sure ad URLs point straight at the page (no redirect chain) and cut mobile load time; every lost click is already paid for.`,
  }),
  SLOW_MOBILE: (p) => ({
    title: 'Slow on mobile',
    body: `Mobile score ${fInt(p, 'perfScore', 'score')}/100 and LCP ${formatMs(pickNum(p, 'lcpMs', 'lcp'))} — past 2.5s visitors bail before they see the offer. Compress the hero image (WebP, right-sized), defer heavy scripts and keep everything above the fold light.`,
  }),
  HIGH_FRICTION: (p) => ({
    title: 'High friction',
    body: `${fInt(p, 'rageClicks', 'rage')} rage clicks and ${fInt(p, 'deadClicks', 'dead')} dead clicks in the period — people click things that do not respond. Watch the friction recordings in Clarity and make those elements clickable, or remove what looks clickable but isn't.`,
  }),
  JS_ERRORS: (p) => ({
    title: 'JS errors on the page',
    body: `${fInt(p, 'scriptErrors', 'count', 'errors')} script errors in the period — one error can kill the booking modal for the whole session. Open Clarity → Errors, find the message and fix it in the theme before raising budget.`,
  }),
  SHALLOW_SCROLL: (p) => ({
    title: 'Shallow scroll',
    body: `The average user sees only ${fPct(p, 'scrollDepthPct', 'scrollDepth')} of the page — everything below your hero is never seen. Move social proof + the offer higher, and cut sections that don't sell.`,
  }),
  LOW_ACTIVE_TIME: (p) => ({
    title: 'Low active time',
    body: `The average session is active for only ${formatSeconds(pickNum(p, 'activeTimeSec', 'activeTime'))} — people skim and leave. Tighten the hero: one promise, one proof, one button — everything that doesn't help the decision goes.`,
  }),
  LOW_MODAL_OPENS: (p) => {
    const rate =
      pickNum(p, 'openRatePct', 'ratePct', 'pct') ??
      pctOf(pickNum(p, 'modalOpens', 'opens'), pickNum(p, 'adLandings', 'landings', 'visits'));
    return {
      title: 'Few open the booking',
      body: `Only ${fInt(p, 'modalOpens', 'opens')} of ${fInt(p, 'adLandings', 'landings', 'visits')} visits open the booking modal (${formatMetric(rate, 'pct')}). The CTA is too weak or too far down — make it visible without scrolling and test more concrete button copy (“Book a free trial”).`,
    };
  },
  QUICKBACK: (p) => ({
    title: 'Many bounce straight back',
    body: `${fInt(p, 'quickbacks', 'count')} sessions leave almost immediately — an expectation break between ad and page. Make the headline repeat the promise of the ad people clicked: same pain, same words, same offer.`,
  }),
  GOOD_SHAPE: () => ({
    title: 'The page is in good shape',
    body: 'No critical findings in the period — speed, behavior and the funnel look healthy. Spend the energy on the ads: fresh creatives and careful scaling on the winners.',
  }),
  OVERLAY_WATCHDOG: (p) => ({
    title: 'Completion signal without a booking',
    body: `${fInt(p, 'count', 'flags', 'watchdogFlags')} visitors got a completion message without any booking existing in Momence — watch the Clarity recordings now.`,
  }),
  ATTEMPTS_NO_COMPLETIONS: (p) => ({
    title: 'No completions',
    body: `${fInt(p, 'opens', 'modalOpens', 'count')} opened the calendar without a single completion in the period.`,
  }),
  CLARITY_QUOTA: () => ({
    title: 'The Clarity quota is used up for today',
    body: 'Data arrives automatically after the next nightly run.',
  }),
  CLARITY_NO_DATA_IN_RANGE: () => ({
    title: 'No Clarity data in the selected period yet',
    body: 'Clarity covers full days (yesterday and older).',
  }),
};

// --- Messages ----------------------------------------------------------------

const MESSAGES = {
  nb: {
    title: 'Landingsside-intelligens',
    sub: 'Daglig cockpit for booking-sidene — trafikk inn, sidehelse, atferd og hva du bør gjøre nå.',
    handleLabels: {
      alle: 'Alle',
      provetime: 'Prøvetime',
      'lege-anbefalt': 'Lege-anbefalt',
      'kroniske-smerter': 'Kroniske smerter',
      'etter-operasjon': 'Etter operasjon',
      'trening-over-70': 'Trening 70+',
    } as Record<Handle, string>,
    funnelTitle: 'Trakten',
    stMetaClicks: 'Meta-klikk',
    stLanded: 'Landet (beacon)',
    stModal: 'Åpnet booking',
    stBooked: 'Booket',
    landRate: 'Land-rate',
    crossLpNote:
      'Meta-klikk er på tvers av alle booking-sider — land-rate beregnes derfor kun for «Alle».',
    healthTitle: 'Sidehelse',
    speedMobile: 'Hastighet mobil',
    speedDesktop: 'Hastighet desktop',
    speedSub: (lcp: string, cls: string) => `LCP ${lcp} · CLS ${cls}`,
    speedUnavailable: 'PageSpeed ikke konfigurert',
    noMeasurement: 'ingen måling ennå',
    jsErrors: 'JS-feil',
    inPeriod: 'i perioden',
    requiresClarity: 'krever Clarity',
    friction: 'Friksjon',
    frictionSub: (rage: string, dead: string) => `${rage} rage · ${dead} døde klikk`,
    behaviorTitle: 'Atferd',
    attemptsTitle: 'Bookingforsøk',
    attemptsSub: 'Egne beacons fra booking-modalen — teller uavhengig av Clarity.',
    attOpens: 'Åpnet kalenderen',
    attCompletions: 'Fullførte',
    attFlags: 'Varsler',
    attFlagsHint: 'Fullførings-signal uten booking i Momence — se opptakene.',
    attRecentTitle: 'Siste forsøk',
    attEmpty: 'Ingen kalenderåpninger i perioden.',
    attOrganic: 'organisk/direkte',
    attInApp: 'app',
    attInAppHint: 'Facebook/Instagram-appens innebygde nettleser',
    attCompletedIn: (min: string) => `fullført på ${min} min`,
    attSeeClarity: 'Se i Clarity',
    attMore: (n: string) => `+${n} til`,
    outcomeBooked: 'Booket',
    outcomePending: 'Uten booking',
    outcomeNoCompletion: 'Åpnet, ikke fullført',
    deviceMobile: 'mobil',
    deviceDesktop: 'desktop',
    deviceUnknown: 'ukjent enhet',
    clarityEmptyTitle: 'Clarity er ikke koblet til',
    clarityEmptyBody:
      'Atferdsdata (scroll, friksjon, JS-feil) krever et eksport-token — hent det under Innstillinger hos Clarity → Data Export.',
    clarityQuotaTitle: 'Clarity-kvoten er brukt for i dag',
    clarityQuotaBody: 'Dataene kommer automatisk etter neste natts kjøring.',
    clarityNoDataTitle: 'Ingen Clarity-data i valgt periode ennå',
    clarityNoDataBody: 'Clarity dekker hele dager (i går og eldre).',
    sessions: 'Økter',
    sessionsSub: (bots: string) => `+ ${bots} bot-økter (filtrert bort)`,
    activeTime: 'Aktiv tid / økt',
    activeOfTotal: (total: string) => `av ${total} total tid`,
    avgPerSession: 'snitt per økt',
    scrollDepth: 'Scrolledybde',
    users: 'Unike brukere',
    sessionsPerUser: (n: string) => `${n} økter per bruker`,
    sessionsPerDay: 'Økter per dag',
    deviceTitle: 'Enhetsfordeling',
    insightsTitle: 'Anbefalinger',
    insightsEmpty: 'Ingen anbefalinger i perioden.',
    insights: INSIGHTS_NB,
    historyTitle: 'Historikk — eksperimenter, endringer & intervjuer',
    experimentsTitle: 'Eksperimenter',
    experimentsSub: (s: string) => `ICE-rangert (Impact × Confidence × Ease). ${s}`,
    statusLabels: {
      idea: 'idé',
      running: 'pågår',
      win: 'vunnet',
      loss: 'tapt',
      done: 'ferdig',
    } as Record<string, string>,
    metric: 'Måltall',
    result: 'Resultat',
    shippedTitle: 'Lanserte endringer',
    personas: 'Målgruppe',
    notes: 'Kundeintervjuer',
    why: 'Hvorfor',
  },
  en: {
    title: 'Landing-page intelligence',
    sub: 'Daily cockpit for the booking pages — traffic in, page health, behavior, and what to do next.',
    handleLabels: {
      alle: 'All',
      provetime: 'Trial session',
      'lege-anbefalt': 'Doctor-recommended',
      'kroniske-smerter': 'Chronic pain',
      'etter-operasjon': 'Post-surgery',
      'trening-over-70': 'Training 70+',
    } as Record<Handle, string>,
    funnelTitle: 'The funnel',
    stMetaClicks: 'Meta clicks',
    stLanded: 'Landed (beacon)',
    stModal: 'Opened booking',
    stBooked: 'Booked',
    landRate: 'Land rate',
    crossLpNote:
      'Meta clicks span all booking pages — land rate is therefore only computed for “All”.',
    healthTitle: 'Page health',
    speedMobile: 'Speed mobile',
    speedDesktop: 'Speed desktop',
    speedSub: (lcp: string, cls: string) => `LCP ${lcp} · CLS ${cls}`,
    speedUnavailable: 'PageSpeed not configured',
    noMeasurement: 'no measurement yet',
    jsErrors: 'JS errors',
    inPeriod: 'in the period',
    requiresClarity: 'requires Clarity',
    friction: 'Friction',
    frictionSub: (rage: string, dead: string) => `${rage} rage · ${dead} dead clicks`,
    behaviorTitle: 'Behavior',
    attemptsTitle: 'Booking attempts',
    attemptsSub: 'Our own beacons from the booking modal — counted independently of Clarity.',
    attOpens: 'Opened the calendar',
    attCompletions: 'Completed',
    attFlags: 'Flags',
    attFlagsHint: 'Completion signal without a booking in Momence — check the recordings.',
    attRecentTitle: 'Latest attempts',
    attEmpty: 'No calendar opens in the period.',
    attOrganic: 'organic/direct',
    attInApp: 'app',
    attInAppHint: 'Facebook/Instagram in-app browser',
    attCompletedIn: (min: string) => `completed in ${min} min`,
    attSeeClarity: 'View in Clarity',
    attMore: (n: string) => `+${n} more`,
    outcomeBooked: 'Booked',
    outcomePending: 'No booking',
    outcomeNoCompletion: 'Opened, not completed',
    deviceMobile: 'mobile',
    deviceDesktop: 'desktop',
    deviceUnknown: 'unknown device',
    clarityEmptyTitle: 'Clarity is not connected',
    clarityEmptyBody:
      'Behavior data (scroll, friction, JS errors) needs an export token — get it under Settings in Clarity → Data Export.',
    clarityQuotaTitle: 'The Clarity quota is used up for today',
    clarityQuotaBody: 'Data arrives automatically after the next nightly run.',
    clarityNoDataTitle: 'No Clarity data in the selected period yet',
    clarityNoDataBody: 'Clarity covers full days (yesterday and older).',
    sessions: 'Sessions',
    sessionsSub: (bots: string) => `+ ${bots} bot sessions (filtered out)`,
    activeTime: 'Active time / session',
    activeOfTotal: (total: string) => `of ${total} total time`,
    avgPerSession: 'avg per session',
    scrollDepth: 'Scroll depth',
    users: 'Distinct users',
    sessionsPerUser: (n: string) => `${n} sessions per user`,
    sessionsPerDay: 'Sessions per day',
    deviceTitle: 'Device split',
    insightsTitle: 'Recommendations',
    insightsEmpty: 'No recommendations for this period.',
    insights: INSIGHTS_EN,
    historyTitle: 'History — experiments, changes & interviews',
    experimentsTitle: 'Experiments',
    experimentsSub: (s: string) => `ICE-ranked (Impact × Confidence × Ease). ${s}`,
    statusLabels: {
      idea: 'idea',
      running: 'running',
      win: 'win',
      loss: 'loss',
      done: 'done',
    } as Record<string, string>,
    metric: 'Metric',
    result: 'Result',
    shippedTitle: 'Shipped changes',
    personas: 'Audience',
    notes: 'Customer interviews',
    why: 'Why',
  },
};

type Msgs = (typeof MESSAGES)['nb'];

// --- Status helpers ----------------------------------------------------------

/** Lighthouse convention: ≥90 good, ≥50 warn, else bad. */
function speedStatus(score: number | null): StatStatus | null {
  if (score == null || !Number.isFinite(score)) return null;
  if (score >= 90) return 'good';
  if (score >= 50) return 'warn';
  return 'bad';
}

function errorStatus(n: number | null): StatStatus | null {
  if (n == null) return null;
  if (n === 0) return 'good';
  if (n < 10) return 'warn';
  return 'bad';
}

function frictionStatus(total: number | null, sessions: number | null): StatStatus | null {
  if (total == null) return null;
  if (sessions != null && sessions > 0) {
    const rate = total / sessions;
    if (rate < 0.02) return 'good';
    if (rate < 0.08) return 'warn';
    return 'bad';
  }
  return total === 0 ? 'good' : null;
}

function insightTone(severity: string, code: string): InsightTone {
  if (code === 'GOOD_SHAPE') return 'good';
  if (severity === 'critical') return 'bad';
  if (severity === 'warn') return 'warn';
  return 'info';
}

const SEVERITY_RANK: Record<string, number> = { critical: 0, warn: 1, info: 2 };

// --- Page --------------------------------------------------------------------

export function LandingPage() {
  const m = useViewMessages(MESSAGES);
  const [handle, setHandle] = useState<Handle>('alle');
  const query = useLanding(handle);

  if (query.isLoading)
    return (
      <PageFrame m={m} handle={handle} onHandle={setHandle}>
        <LandingSkeleton />
      </PageFrame>
    );
  if (query.isError || !query.data)
    return (
      <PageFrame m={m} handle={handle} onHandle={setHandle}>
        <ErrorState onRetry={() => query.refetch()} />
      </PageFrame>
    );

  const d = query.data;

  return (
    <PageFrame m={m} handle={handle} onHandle={setHandle}>
      <TraktZone m={m} d={d} handle={handle} />
      <HealthZone m={m} d={d} />
      <BehaviorZone m={m} d={d} />
      <InsightsZone m={m} insights={d.insights} />
      <HistorikkZone m={m} playbook={d.playbook} />
    </PageFrame>
  );
}

// --- Zone 0: frame + LP selector ---------------------------------------------

function PageFrame({
  m,
  handle,
  onHandle,
  children,
}: {
  m: Msgs;
  handle: Handle;
  onHandle: (h: Handle) => void;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-title text-fg">{m.title}</h1>
          <p className="mt-1 text-body text-fg-muted">{m.sub}</p>
        </div>
        <HandleToggle m={m} value={handle} onChange={onHandle} />
      </header>
      {children}
    </div>
  );
}

function HandleToggle({
  m,
  value,
  onChange,
}: {
  m: Msgs;
  value: Handle;
  onChange: (h: Handle) => void;
}) {
  return (
    <div className="flex w-full items-center gap-0.5 overflow-x-auto rounded-md border border-border bg-surface p-0.5 sm:w-auto sm:flex-wrap">
      {HANDLE_ORDER.map((h) => (
        <button
          key={h}
          type="button"
          onClick={() => onChange(h)}
          aria-pressed={h === value}
          className={cn(
            'flex h-9 shrink-0 items-center whitespace-nowrap rounded px-2.5 text-label transition-fast sm:h-7',
            h === value ? 'bg-surface-3 font-medium text-fg' : 'text-fg-subtle hover:text-fg',
          )}
        >
          {m.handleLabels[h]}
        </button>
      ))}
    </div>
  );
}

function LandingSkeleton() {
  const cards = ['sk-a', 'sk-b', 'sk-c', 'sk-d'];
  return (
    <div aria-busy="true" className="flex flex-col gap-6">
      <Skeleton className="h-28" />
      <div className="grid grid-cols-2 gap-2 sm:gap-3 xl:grid-cols-4">
        {cards.map((k) => (
          <Skeleton key={k} className="h-24" />
        ))}
      </div>
      <TableSkeleton rows={4} />
    </div>
  );
}

// --- Zone 1: Trakten ----------------------------------------------------------

function TraktZone({ m, d, handle }: { m: Msgs; d: LandingData; handle: Handle }) {
  const t = d.funnel.totals;
  const stages: FunnelStageItem[] = [
    {
      key: 'clicks',
      label: m.stMetaClicks,
      value: t.metaClicks ?? null,
      barClass: 'bg-accent-click',
    },
    { key: 'landed', label: m.stLanded, value: t.adLandings ?? null, barClass: 'bg-accent-lead' },
    { key: 'modal', label: m.stModal, value: t.modalOpens ?? null, barClass: 'bg-accent-booking' },
    {
      key: 'booked',
      label: (
        <span className="inline-flex items-center gap-1">
          {m.stBooked}
          <CaveatRef id="trustMeta" />
        </span>
      ),
      value: t.matchedBookings ?? null,
      barClass: 'bg-brand',
    },
  ];
  // Per-LP, Meta clicks are cross-site → the click→land % would be a lie; the
  // API only emits landRatePct for 'alle'. null forces the em dash.
  const landConnector = handle === 'alle' ? (d.funnel.rates.landRatePct ?? undefined) : null;

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-section text-fg">{m.funnelTitle}</h2>
        {handle === 'alle' && d.funnel.rates.landRatePct != null ? (
          <Badge tone="neutral">
            {m.landRate}: {formatMetric(d.funnel.rates.landRatePct, 'pct')}
          </Badge>
        ) : null}
      </div>
      <Card className="p-4">
        <FunnelStages stages={stages} connectors={[landConnector]} />
      </Card>
      {handle !== 'alle' ? <p className="text-label text-fg-subtle">{m.crossLpNote}</p> : null}
    </section>
  );
}

// --- Zone 2: Sidehelse ---------------------------------------------------------

function HealthZone({ m, d }: { m: Msgs; d: LandingData }) {
  const totals = d.clarity.available ? d.clarity.totals : null;
  const scriptErrors = totals?.scriptErrors ?? null;
  const rage = totals?.rageClicks ?? null;
  const dead = totals?.deadClicks ?? null;
  const frictionTotal = rage == null && dead == null ? null : (rage ?? 0) + (dead ?? 0);

  const jsStatus = errorStatus(scriptErrors);
  const frStatus = frictionStatus(frictionTotal, totals?.sessions ?? null);

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-section text-fg">{m.healthTitle}</h2>
      <div className="grid grid-cols-2 gap-2 sm:gap-3 xl:grid-cols-4">
        <SpeedCard
          m={m}
          label={m.speedMobile}
          sample={d.speed.latest.mobile}
          available={d.speed.available}
        />
        <SpeedCard
          m={m}
          label={m.speedDesktop}
          sample={d.speed.latest.desktop}
          available={d.speed.available}
        />
        <StatCard
          label={m.jsErrors}
          value={formatMetric(scriptErrors, 'int')}
          sub={totals ? m.inPeriod : m.requiresClarity}
          {...(jsStatus ? { status: jsStatus } : {})}
        />
        <StatCard
          label={m.friction}
          value={formatMetric(frictionTotal, 'int')}
          sub={
            totals
              ? m.frictionSub(formatMetric(rage, 'int'), formatMetric(dead, 'int'))
              : m.requiresClarity
          }
          {...(frStatus ? { status: frStatus } : {})}
        />
      </div>
    </section>
  );
}

function SpeedCard({
  m,
  label,
  sample,
  available,
}: {
  m: Msgs;
  label: string;
  sample: SpeedSample | null;
  available: boolean;
}) {
  const score = sample?.perfScore ?? null;
  const status = speedStatus(score);
  const sub = !available
    ? m.speedUnavailable
    : sample
      ? m.speedSub(formatMs(sample.lcpMs), formatCls(sample.cls))
      : m.noMeasurement;
  return (
    <StatCard
      label={label}
      value={formatMetric(score, 'int')}
      sub={sub}
      {...(status ? { status } : {})}
    />
  );
}

// --- Zone 3: Atferd — Bookingforsøk (egne beacons) + Clarity-aggregater ---------

type Attempts = LandingData['attempts'];
type Attempt = Attempts['recent'][number];

const ATTEMPT_AD_LABEL_MAX = 26;
const ATTEMPTS_VISIBLE_ROWS = 15;

function BehaviorZone({ m, d }: { m: Msgs; d: LandingData }) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-section text-fg">{m.behaviorTitle}</h2>
      <AttemptsBlock m={m} attempts={d.attempts} />
      {d.clarity.available ? (
        <ClarityCards m={m} d={d} />
      ) : (
        <ClarityEmpty m={m} status={d.clarityStatus} />
      )}
    </section>
  );
}

// --- Bookingforsøk: the per-attempt journal from OUR beacons -------------------

function AttemptsBlock({ m, attempts }: { m: Msgs; attempts: Attempts }) {
  const [showAll, setShowAll] = useState(false);
  const opens = attempts.days.reduce((s, day) => s + day.opens, 0);
  const completions = attempts.days.reduce((s, day) => s + day.completions, 0);
  const flags = attempts.days.reduce((s, day) => s + day.watchdogFlags, 0);

  const visible = showAll ? attempts.recent : attempts.recent.slice(0, ATTEMPTS_VISIBLE_ROWS);
  const overflow = attempts.recent.length - visible.length;

  return (
    <div className="flex flex-col gap-3">
      <div>
        <h3 className="text-body font-semibold text-fg">{m.attemptsTitle}</h3>
        <p className="mt-0.5 text-label text-fg-subtle">{m.attemptsSub}</p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard label={m.attOpens} value={formatMetric(opens, 'int')} sub={m.inPeriod} />
        <StatCard
          label={m.attCompletions}
          value={formatMetric(completions, 'int')}
          sub={m.inPeriod}
          {...(completions > 0 ? { status: 'good' as const } : {})}
        />
        <StatCard
          label={m.attFlags}
          value={formatMetric(flags, 'int')}
          sub={m.attFlagsHint}
          {...(flags > 0 ? { status: 'bad' as const } : {})}
        />
      </div>

      <Card className="p-4">
        <h3 className="text-label font-semibold text-fg-muted">{m.attRecentTitle}</h3>
        {attempts.recent.length === 0 ? (
          <p className="mt-2 text-label text-fg-muted">{m.attEmpty}</p>
        ) : (
          <>
            <ul className="mt-1 divide-y divide-border/40">
              {visible.map((a) => (
                <AttemptRow key={attemptKey(a)} a={a} m={m} />
              ))}
            </ul>
            {overflow > 0 ? (
              <button
                type="button"
                onClick={() => setShowAll(true)}
                className="mt-1.5 inline-flex min-h-9 items-center text-micro text-fg-subtle transition-fast hover:text-fg sm:min-h-0"
              >
                {m.attMore(formatMetric(overflow, 'int'))}
              </button>
            ) : null}
          </>
        )}
      </Card>
    </div>
  );
}

/** Stable-enough list key from the row's own fields (no array index — biome). */
function attemptKey(a: Attempt): string {
  return `${a.at}|${a.device}|${a.outcome}|${a.ad?.adId ?? ''}|${a.clarityLink}`;
}

function AttemptRow({ a, m }: { a: Attempt; m: Msgs }) {
  // formatBookedAt → 'dd.MM HH:mm'; stacked date/clock, mirroring BookingList.
  const ts = formatBookedAt(a.at);
  const sp = ts.lastIndexOf(' ');
  const datePart = sp > 0 ? ts.slice(0, sp) : ts;
  const clockPart = sp > 0 ? ts.slice(sp + 1) : null;

  return (
    <li className="grid grid-cols-[3.5rem_minmax(0,1fr)_auto] items-start gap-x-2 py-2 sm:grid-cols-[4.5rem_minmax(0,1fr)_auto] sm:gap-x-3">
      <span className="flex flex-col leading-tight">
        <span className="text-label tabular text-fg-subtle">{datePart}</span>
        {clockPart ? <span className="text-micro tabular text-fg-subtle">{clockPart}</span> : null}
      </span>
      <span className="min-w-0">
        <span className="flex min-w-0 items-center gap-1.5">
          <DeviceGlyph device={a.device} m={m} />
          {a.inApp ? (
            <Badge tone="neutral" title={m.attInAppHint}>
              {m.attInApp}
            </Badge>
          ) : null}
          <AttemptAdChip ad={a.ad} m={m} />
        </span>
        {a.minutesToComplete != null ? (
          <span className="mt-0.5 block text-micro text-fg-subtle">
            {m.attCompletedIn(formatCount1(a.minutesToComplete))}
          </span>
        ) : null}
      </span>
      <span className="flex min-w-0 shrink flex-col items-end gap-1">
        <OutcomeBadge outcome={a.outcome} m={m} />
        <a
          href={a.clarityLink}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-h-9 items-center text-right text-micro text-fg-subtle transition-fast hover:text-fg sm:min-h-0"
        >
          {m.attSeeClarity} ↗
        </a>
      </span>
    </li>
  );
}

function DeviceGlyph({ device, m }: { device: Attempt['device']; m: Msgs }) {
  const Icon = device === 'mobile' ? Smartphone : device === 'desktop' ? Monitor : CircleHelp;
  const label =
    device === 'mobile' ? m.deviceMobile : device === 'desktop' ? m.deviceDesktop : m.deviceUnknown;
  return (
    <span title={label} className="shrink-0">
      <Icon className="size-3.5 text-fg-subtle" aria-hidden />
      <span className="sr-only">{label}</span>
    </span>
  );
}

/** Ad identity chip (name ?? id, campaign in the title); ad-less attempts show
 * a muted "organisk/direkte" instead — never an empty hole. */
function AttemptAdChip({ ad, m }: { ad: Attempt['ad']; m: Msgs }) {
  const raw = ad?.adName ?? ad?.adId ?? '';
  if (!raw) return <span className="truncate text-micro text-fg-subtle">{m.attOrganic}</span>;
  const label =
    raw.length > ATTEMPT_AD_LABEL_MAX ? `${raw.slice(0, ATTEMPT_AD_LABEL_MAX - 1)}…` : raw;
  return (
    <Badge tone="booking" title={ad?.campaign ?? undefined} className="min-w-0">
      <span className="truncate">{label}</span>
    </Badge>
  );
}

function OutcomeBadge({ outcome, m }: { outcome: Attempt['outcome']; m: Msgs }) {
  if (outcome === 'booked') {
    return (
      <Badge tone="good">
        <span aria-hidden>✓</span> {m.outcomeBooked}
      </Badge>
    );
  }
  if (outcome === 'pending_no_booking') {
    return (
      <Badge tone="bad" title={m.attFlagsHint}>
        <span aria-hidden>⚠</span> {m.outcomePending}
      </Badge>
    );
  }
  return <Badge tone="neutral">{m.outcomeNoCompletion}</Badge>;
}

// --- Clarity aggregates (honest empty state via clarityStatus) ------------------

function ClarityEmpty({ m, status }: { m: Msgs; status: LandingData['clarityStatus'] }) {
  // 'ok' with no rows shouldn't happen — degrade to the no-data copy, never lie
  // about the token being missing.
  const copy =
    status === 'quota'
      ? { title: m.clarityQuotaTitle, body: m.clarityQuotaBody }
      : status === 'not_configured'
        ? { title: m.clarityEmptyTitle, body: m.clarityEmptyBody }
        : { title: m.clarityNoDataTitle, body: m.clarityNoDataBody };
  return (
    <Card>
      <EmptyState title={copy.title}>{copy.body}</EmptyState>
    </Card>
  );
}

function ClarityCards({ m, d }: { m: Msgs; d: LandingData }) {
  const t = d.clarity.totals;
  const perUser = t && t.distinctUsers > 0 ? t.sessions / t.distinctUsers : null;
  const days = d.clarity.days;
  const devices = d.clarity.deviceSplit?.rows ?? [];

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-2 sm:gap-3 xl:grid-cols-4">
        <StatCard
          label={m.sessions}
          value={formatMetric(t?.sessions ?? null, 'int')}
          sub={t ? m.sessionsSub(formatMetric(t.botSessions, 'int')) : undefined}
        />
        <StatCard
          label={m.activeTime}
          value={formatSeconds(t?.activeTimeSec ?? null)}
          sub={
            t?.totalTimeSec != null
              ? m.activeOfTotal(formatSeconds(t.totalTimeSec))
              : m.avgPerSession
          }
        />
        <StatCard
          label={m.scrollDepth}
          value={formatMetric(t?.scrollDepthPct ?? null, 'pct')}
          sub={<ScrollBar pct={t?.scrollDepthPct ?? null} />}
        />
        <StatCard
          label={m.users}
          value={formatMetric(t?.distinctUsers ?? null, 'int')}
          sub={perUser != null ? m.sessionsPerUser(formatCount1(perUser)) : undefined}
        />
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {devices.length > 0 ? (
          <Card className="p-4">
            <h3 className="text-label font-semibold text-fg-muted">{m.deviceTitle}</h3>
            <div className="mt-2">
              <DeviceTable rows={devices} />
            </div>
          </Card>
        ) : null}
        {days.length > 0 ? (
          <Card className="p-4">
            <h3 className="text-label font-semibold text-fg-muted">{m.sessionsPerDay}</h3>
            <div className="mt-3">
              <Sparkline
                values={days.map((x) => x.sessions ?? null)}
                color={ACCENT.click}
                height={56}
              />
            </div>
          </Card>
        ) : null}
      </div>
    </div>
  );
}

function ScrollBar({ pct }: { pct: number | null }) {
  if (pct == null || !Number.isFinite(pct)) return null;
  const w = Math.max(0, Math.min(100, pct));
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-3">
      <div className="h-full rounded-full bg-accent-click" style={{ width: `${w}%` }} />
    </div>
  );
}

// --- Zone 4: Anbefalinger -------------------------------------------------------

function InsightsZone({ m, insights }: { m: Msgs; insights: LandingData['insights'] }) {
  const sorted = [...insights].sort(
    (a, b) => (SEVERITY_RANK[a.severity] ?? 3) - (SEVERITY_RANK[b.severity] ?? 3),
  );
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-section text-fg">{m.insightsTitle}</h2>
      {sorted.length === 0 ? (
        <p className="text-label text-fg-subtle">{m.insightsEmpty}</p>
      ) : (
        <div className="flex flex-col gap-2">
          {sorted.map((ins) => {
            const tpl = m.insights[ins.code];
            const copy = tpl ? tpl(ins.params) : { title: ins.code, body: '' };
            return (
              <InsightCard
                key={ins.code}
                tone={insightTone(ins.severity, ins.code)}
                title={copy.title}
              >
                {copy.body || null}
              </InsightCard>
            );
          })}
        </div>
      )}
    </section>
  );
}

// --- Zone 5: Historikk (collapsed) ----------------------------------------------

function HistorikkZone({ m, playbook }: { m: Msgs; playbook: Playbook }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="rounded-lg border border-border bg-surface">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 rounded-lg px-4 py-3 text-left transition-fast hover:bg-surface-2"
      >
        {open ? (
          <ChevronDown className="size-4 shrink-0 text-fg-subtle" aria-hidden />
        ) : (
          <ChevronRight className="size-4 shrink-0 text-fg-subtle" aria-hidden />
        )}
        <span className="text-section text-fg">{m.historyTitle}</span>
      </button>
      {open ? (
        <div className="flex flex-col gap-6 border-t border-border p-4">
          <Experiments
            m={m}
            experiments={playbook.experiments}
            summary={playbook.experimentsSummary}
          />
          <Shipped m={m} shipped={playbook.shipped} />
          <Intake m={m} intake={playbook.intake} />
        </div>
      ) : null}
    </section>
  );
}

function Experiments({
  m,
  experiments,
  summary,
}: {
  m: Msgs;
  experiments: Playbook['experiments'];
  summary: Playbook['experimentsSummary'];
}) {
  const tone = (s: string) =>
    s === 'win' ? 'good' : s === 'loss' ? 'bad' : s === 'running' ? 'brand' : 'neutral';
  const summaryText = ['idea', 'running', 'win', 'loss', 'done']
    .filter((k) => (summary[k] ?? 0) > 0)
    .map((k) => `${summary[k]} ${m.statusLabels[k] ?? k}`)
    .join(' · ');

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <FlaskConical className="size-4 text-fg-muted" aria-hidden />
        <h3 className="text-body font-semibold text-fg">{m.experimentsTitle}</h3>
      </div>
      <p className="text-label text-fg-muted">{m.experimentsSub(summaryText)}</p>
      {experiments.length === 0 ? (
        <p className="text-label text-fg-subtle">—</p>
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {experiments.map((x) => (
            <Card key={x.id} className="flex flex-col gap-2 p-4">
              <div className="flex items-start justify-between gap-2">
                <h4 className="text-body font-medium text-fg">{x.title}</h4>
                <Badge tone={tone(x.status)}>{m.statusLabels[x.status] ?? x.status}</Badge>
              </div>
              <p className="text-label text-fg-muted">{x.hypothesis}</p>
              <div className="flex flex-wrap items-center gap-1.5">
                <Badge tone="neutral">{x.where}</Badge>
                <Badge tone="neutral">
                  {m.metric}: {x.metric}
                </Badge>
                {x.evidence.map((ev) => (
                  <Badge key={ev} tone="neutral">
                    {ev}
                  </Badge>
                ))}
              </div>
              <div className="flex items-center gap-3 text-micro text-fg-subtle tabular">
                <span>ICE {x.ice}</span>
                <span>I {x.impact}</span>
                <span>C {x.confidence}</span>
                <span>E {x.ease}</span>
              </div>
              {x.result ? (
                <p className="flex items-center gap-1.5 text-label text-good-fg">
                  <CircleCheck className="size-3.5" aria-hidden />
                  {m.result}: {x.result.before} → {x.result.after} (
                  {x.result.deltaPct > 0 ? '+' : ''}
                  {x.result.deltaPct}%) — {x.result.note}
                </p>
              ) : null}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function Shipped({ m, shipped }: { m: Msgs; shipped: Playbook['shipped'] }) {
  if (shipped.changes.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-body font-semibold text-fg">{m.shippedTitle}</h3>
      <div className="flex flex-col gap-3">
        {shipped.changes.map((c) => (
          <div key={c.id} className="border-b border-border/60 pb-3 last:border-0">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="neutral">{c.area}</Badge>
              <span className="text-label font-medium text-fg">{c.title}</span>
              <Badge
                tone={c.status === 'live' ? 'good' : c.status === 'preview' ? 'warn' : 'neutral'}
              >
                {c.status}
              </Badge>
            </div>
            <p className="mt-1 text-micro text-fg-subtle">
              {c.before} → {c.after}
            </p>
            <p className="mt-1 text-micro text-fg-muted">
              {m.why}: {c.why}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Intake({ m, intake }: { m: Msgs; intake: Playbook['intake'] }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-label text-fg-muted">{m.personas}:</span>
        {intake.personaTags.map((tag) => (
          <Badge key={tag} tone="neutral">
            {tag}
          </Badge>
        ))}
      </div>
      {intake.notes.length > 0 ? (
        <div className="flex flex-col gap-2">
          <h3 className="text-label font-semibold text-fg-muted">{m.notes}</h3>
          {intake.notes.map((n) => (
            <div key={n.id} className="text-label">
              <span className="text-fg-subtle tabular">{n.date}</span>
              {n.tag ? (
                <Badge tone="neutral" className="mx-1.5">
                  {n.tag}
                </Badge>
              ) : (
                ' '
              )}
              <span className="text-fg-muted">{n.text}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
